import { ManagerBase, MouseEventResult } from './manager-base';
import { LevelDesignerElementBase } from '../elements/element-base';
import { PolygonElement } from '../elements/polygon-element';

export class RotationManager extends ManagerBase {
    private isRotating: boolean = false;
    private rotationCenterX: number = 0;
    private rotationCenterY: number = 0;
    private rotationStartAngle: number = 0;
    private currentRotationAngle: number = 0;
    private lastSnappedAngle: number = 0;
    private isShiftPressed: boolean = false;
    private isCtrlPressed: boolean = false;

    constructor(levelDesigner: any, canvas: HTMLCanvasElement) {
        super(levelDesigner, canvas);
    }

    public OnDraw(): void {
        if (this.isRotating) return; // Don't draw the handle while rotating

        const selectedElements = this.levelDesigner.selectedElements;
        if (selectedElements.length === 0) return;

        // Check if any selected element is locked
        const anyElementLocked = selectedElements.some(element => element.isLocked());
        if (anyElementLocked) return; // Don't draw the handle if any element is locked

        const handlePos = this.getRotateHandlePosition(selectedElements);
        this.drawRotateHandle(handlePos.x, handlePos.y);
    }

    public override handleMouseDown(event: MouseEvent): MouseEventResult | undefined {
        if (event.button === 1) return undefined;
        const mousePos = this.getMousePosition(event);
        if (this.isOverRotateHandle(mousePos.x, mousePos.y) && !this.levelDesigner.selectedElements[0].isLocked()) {
            this.isRotating = true;
            this.setRotationCenter();
            this.rotationStartAngle = Math.atan2(mousePos.y - this.rotationCenterY, mousePos.x - this.rotationCenterX);
            return { cursor: 'grabbing', stopPropagation: true, redraw: false };
        }
        return undefined;
    }

    public override handleMouseMove(event: MouseEvent): MouseEventResult | undefined {
        const mousePos = this.getMousePosition(event);
        if (this.isRotating) {
            this.rotateElements(mousePos.x, mousePos.y);
            return { cursor: 'grabbing', stopPropagation: true, redraw: true };
        } else if (this.isOverRotateHandle(mousePos.x, mousePos.y)) {
            return { cursor: 'grab', stopPropagation: false, redraw: false };
        }
        return undefined;
    }

    public override handleMouseUp(event: MouseEvent): MouseEventResult | undefined {
        if (this.isRotating) {
            this.isRotating = false;
            this.levelDesigner.redraw();
            this.levelDesigner.saveState();
            return { cursor: 'default', stopPropagation: true, redraw: false };
        }
        return undefined;
    }

    private drawSingleElementRotateHandle(element: LevelDesignerElementBase): void {
        const handlePos = (element as PolygonElement).getRotateHandlePosition();
        this.drawRotateHandle(handlePos.x, handlePos.y);
    }

    private drawMultiElementRotateHandle(elements: LevelDesignerElementBase[]): void {
        const handlePos = this.getMultiElementRotateHandlePosition(elements);
        this.drawRotateHandle(handlePos.x, handlePos.y);
    }

    private getMultiElementRotateHandlePosition(elements: LevelDesignerElementBase[]): { x: number, y: number } {
        const bbox = this.getBoundingBoxForElements(elements);
        const centerX = bbox.x + bbox.width / 2;
        const centerY = bbox.y + bbox.height / 2;
        const handleDistance = 20 / this.levelDesigner.getScale();
        return {
            x: centerX,
            y: centerY - handleDistance
        };
    }

    private drawRotateHandle(x: number, y: number): void {
        const scale = this.levelDesigner.getScale();
        const size = 16 / scale;
        
        this.ctx.save();
        this.ctx.strokeStyle = 'green';
        this.ctx.fillStyle = 'green';
        this.ctx.lineWidth = 2 / scale;
        
        // Draw the circular part
        this.ctx.beginPath();
        this.ctx.arc(x, y, size / 2, 0, 1.5 * Math.PI);
        this.ctx.stroke();
        
        // Calculate arrow tip position
        const arrowTipX = x;
        const arrowTipY = y - size / 2;
        
        // Draw the arrow with scaled dimensions
        const arrowSize = size / 4;
        this.ctx.beginPath();
        this.ctx.moveTo(arrowTipX, arrowTipY);
        this.ctx.lineTo(arrowTipX - arrowSize, arrowTipY - arrowSize);
        this.ctx.lineTo(arrowTipX + arrowSize, arrowTipY - arrowSize);
        this.ctx.closePath();
        this.ctx.fill();
        
        this.ctx.restore();
    }

    private getBoundingBoxForElements(elements: LevelDesignerElementBase[]): { x: number, y: number, width: number, height: number } {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        elements.forEach(element => {
            const bbox = element.getBoundingBox();
            minX = Math.min(minX, bbox.x);
            minY = Math.min(minY, bbox.y);
            maxX = Math.max(maxX, bbox.x + bbox.width);
            maxY = Math.max(maxY, bbox.y + bbox.height);
        });
        return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }

    private isOverRotateHandle(x: number, y: number): boolean {
        const selectedElements = this.levelDesigner.selectedElements;
        if (selectedElements.length === 0) return false;

        const handlePos = this.getRotateHandlePosition(selectedElements);
        const dx = x - handlePos.x;
        const dy = y - handlePos.y;
        return Math.sqrt(dx * dx + dy * dy) <= 8 / this.levelDesigner.getScale();
    }

    private setRotationCenter(): void {
        const selectedElements = this.levelDesigner.selectedElements;
        if (selectedElements.length === 0) return;

        // Get the current rotation from the first element (it's in degrees)
        const firstElement = selectedElements[0];
        this.currentRotationAngle = firstElement.getDisplayAngle() * Math.PI / 180; // Convert to radians
        this.lastSnappedAngle = this.currentRotationAngle;

        if (selectedElements.length === 1) {
            const element = selectedElements[0];
            const bbox = element.getBoundingBox();
            this.rotationCenterX = bbox.x + bbox.width / 2;
            this.rotationCenterY = bbox.y + bbox.height / 2;
        } else if (selectedElements.length > 1) {
            const bbox = this.getBoundingBoxForElements(selectedElements);
            this.rotationCenterX = bbox.x + bbox.width / 2;
            this.rotationCenterY = bbox.y + bbox.height / 2;
        }
    }

    private rotateElements(mouseX: number, mouseY: number): void {
        const currentAngle = Math.atan2(mouseY - this.rotationCenterY, mouseX - this.rotationCenterX);
        let deltaAngle = currentAngle - this.rotationStartAngle;

        // Normalize delta angle to be between -PI and PI
        if (deltaAngle > Math.PI) deltaAngle -= 2 * Math.PI;
        if (deltaAngle < -Math.PI) deltaAngle += 2 * Math.PI;

        // Convert the current angle to degrees for the element's rotation
        const currentAngleDegrees = currentAngle * 180 / Math.PI;
        this.currentRotationAngle = currentAngleDegrees;

        let finalRotation = this.currentRotationAngle;
        let rotationDelta = deltaAngle * 180 / Math.PI; // Convert delta to degrees

        if (this.isShiftPressed) {
            const snapAngle = 45; // 45 degrees for snapping
            const snapThreshold = snapAngle / 2;
            const diffFromLastSnap = finalRotation - this.lastSnappedAngle;

            if (Math.abs(diffFromLastSnap) >= snapThreshold) {
                const newSnappedAngle = Math.round(finalRotation / snapAngle) * snapAngle;
                rotationDelta = newSnappedAngle - this.lastSnappedAngle;
                this.lastSnappedAngle = newSnappedAngle;
                finalRotation = newSnappedAngle;
            } else {
                rotationDelta = 0;
            }
        } else {
            this.lastSnappedAngle = finalRotation;
        }

        if (rotationDelta !== 0) {
            if (this.isCtrlPressed) {
                // Rotate each element around its own center
                this.levelDesigner.selectedElements.forEach(element => {
                    const bbox = element.getBoundingBox();
                    const elementCenterX = bbox.x + bbox.width / 2;
                    const elementCenterY = bbox.y + bbox.height / 2;
                    element.rotate(rotationDelta, elementCenterX, elementCenterY);
                    // Update form values immediately
                    element.updateForm();
                });
            } else {
                // Rotate all elements around group center
                this.levelDesigner.selectedElements.forEach(element => {
                    element.rotate(rotationDelta, this.rotationCenterX, this.rotationCenterY);
                    // Update form values immediately
                    element.updateForm();
                });
            }

            // Update the property editor if it exists
            const propertyEditor = this.levelDesigner.getPropertyEditor();
            if (propertyEditor) {
                propertyEditor.forceUpdate();
            }
        }

        this.rotationStartAngle = currentAngle;
        this.levelDesigner.redraw();
    }

    public override handleKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Shift') {
            this.isShiftPressed = true;
        }
        if (event.key === 'Control') {
            this.isCtrlPressed = true;
        }
    }

    public override handleKeyUp(event: KeyboardEvent): void {
        if (event.key === 'Shift') {
            this.isShiftPressed = false;
        }
        if (event.key === 'Control') {
            this.isCtrlPressed = false;
        }
    }

    private getRotateHandlePosition(elements: LevelDesignerElementBase[]): { x: number, y: number } {
        const bbox = this.getBoundingBoxForElements(elements);
        const centerX = bbox.x + bbox.width / 2;
        const topY = bbox.y;
        const handleDistance = 40 / this.levelDesigner.getScale();

        return {
            x: centerX,
            y: topY - handleDistance
        };
    }
}
